import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Settings, Shield, MessageSquare, MessageCircle, Loader2, Phone, Clock, Star, CreditCard, Users, Plus, Trash2, Pencil, Banknote, Key, Copy, RefreshCw, ToggleLeft, Eye, EyeOff, Mail, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';

import { BACKEND_URL, API } from '../utils/apiConfig';

// Per-category WhatsApp message types — used to render individual toggles in the
// "WhatsApp Notifications" section. Each entry surfaces the template name to admin
// for reference. Keys must match the backend `WA_CATEGORY_KEYS` and the keys in
// `templateNames` state.
const WA_CATEGORY_META = {
  booking_confirmation: {
    label: 'MAKS Booking — Booking Confirmation',
    description: 'Sent for Admin Panel, Website, WhatsApp, MAXIO and all other internal booking sources.',
    trigger: 'On booking create',
  },
  booking_confirmation_online: {
    label: 'Online Booking — Booking Confirmation',
    description: 'Sent for bookings coming from District, KheloMore, Hudle, and Email.',
    trigger: 'On booking create',
  },
  venue_admin_new_booking: {
    label: 'Venue Admin — New Booking Alert',
    description: 'Alerts venue-mapped WhatsApp recipients whenever a MAKS Booking is created (admin/app/website/WhatsApp/MAXIO). Online-source bookings are skipped.',
    trigger: 'On MAKS booking create',
  },
  tournament_confirm: {
    label: 'Event Registration Confirmation',
    description: 'Sent to players (and partners for Doubles) immediately after a tournament/event registration is completed.',
    trigger: 'On tournament registration',
  },
  event_participant_update: {
    label: 'Event Update Notification',
    description: 'Manual blast from the Event → Send Update dialog to selected participants (player / partner / both).',
    trigger: 'Admin-triggered manual blast',
  },
  booking_reminder: {
    label: 'Booking Reminder (1 hour before)',
    description: 'Sent 60 minutes before the slot starts.',
    trigger: 'Scheduler — 1 hour pre-slot',
  },
  review_bhayandar: {
    label: 'Review — Bhayandar',
    description: 'Post-game Google review request for Bhayandar players.',
    trigger: 'Scheduler — after slot ends',
  },
  review_isf: {
    label: 'Review — ISF (Mira Road)',
    description: 'Post-game Google review request for ISF players.',
    trigger: 'Scheduler — after slot ends',
  },
  review_wilfred: {
    label: 'Review — Sports Complex (Wilfred)',
    description: 'Post-game Google review request for Sports Complex players.',
    trigger: 'Scheduler — after slot ends',
  },
};

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [llmConfig, setLlmConfig] = useState(null);
  const [llmOptions, setLlmOptions] = useState({});
  const [llmSaving, setLlmSaving] = useState(false);
  const [otpUpdatedBy, setOtpUpdatedBy] = useState(null);
  const [otpUpdatedAt, setOtpUpdatedAt] = useState(null);
  const [waTemplate, setWaTemplate] = useState(null);
  const [waLoading, setWaLoading] = useState(false);
  const [waReminder, setWaReminder] = useState(null);
  const [waReminderLoading, setWaReminderLoading] = useState(false);
  const [waReview, setWaReview] = useState(null);
  const [waReviewLoading, setWaReviewLoading] = useState(false);
  const [cashfreeEnabled, setCashfreeEnabled] = useState(false);
  const [cashfreeSettings, setCashfreeSettings] = useState(null);
  const [cashfreeSaving, setCashfreeSaving] = useState(false);
  const [payAtVenueEnabled, setPayAtVenueEnabled] = useState(true);
  const [payAtVenueSaving, setPayAtVenueSaving] = useState(false);
  const [payAtVenueMeta, setPayAtVenueMeta] = useState(null);
  const [venueRecipients, setVenueRecipients] = useState([]);
  const [venueList, setVenueList] = useState([]);
  const [vnLoading, setVnLoading] = useState(false);
  const [vnForm, setVnForm] = useState({ name: '', mobile: '', venues: [] });
  const [vnEditing, setVnEditing] = useState(null);
  const [vnShowForm, setVnShowForm] = useState(false);
  const [maksioRecipients, setMaksioRecipients] = useState([]);
  const [maksioLoading, setMaksioLoading] = useState(false);
  const [maksioForm, setMaksioForm] = useState({ name: '', mobile: '', is_active: true });
  const [maksioEditing, setMaksioEditing] = useState(null);
  const [maksioShowForm, setMaksioShowForm] = useState(false);
  const [waEnabled, setWaEnabled] = useState(false);
  const [waToggleLoading, setWaToggleLoading] = useState(false);
  const [waToggleMeta, setWaToggleMeta] = useState(null);

  // Per-template (category) toggles. Defaults to all-on so existing behaviour
  // is preserved until admin explicitly disables a category.
  const [waCategoryToggles, setWaCategoryToggles] = useState({
    booking_confirmation: true,
    booking_confirmation_online: true,
    venue_admin_new_booking: true,
    booking_reminder: true,
    tournament_confirm: true,
    event_participant_update: true,
    review_bhayandar: true,
    review_isf: true,
    review_wilfred: true,
  });
  const [waCategoryLoading, setWaCategoryLoading] = useState(null); // key currently saving
  // Review cooldown (days) — prevents re-sending review WhatsApp to the same
  // contact within N days. 0 disables the cooldown.
  const [reviewCooldownDays, setReviewCooldownDays] = useState(30);
  const [reviewCooldownInput, setReviewCooldownInput] = useState('30');
  const [reviewCooldownSaving, setReviewCooldownSaving] = useState(false);
  const [templateNames, setTemplateNames] = useState({
    booking_confirmation: 'bookingconfirmation_for_maksapp_user_new_1',
    booking_confirmation_online: '',
    venue_admin_new_booking: 'notifyvenueadmin',
    booking_reminder: 'maks_booking_reminder',
    tournament_confirm: 'tournamentconfirm',
    event_participant_update: 'event_participant_update',
    review_bhayandar: 'maks_review_bhayandar',
    review_isf: 'maks_review_isf',
    review_wilfred: 'maks_review_wilfred',
  });
  const [tplEditing, setTplEditing] = useState(false);
  const [tplSaving, setTplSaving] = useState(false);
  const [templateConfig, setTemplateConfig] = useState({});

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState({ partner_name: '' });
  const [apiKeyShowForm, setApiKeyShowForm] = useState(false);
  const [apiKeyCreating, setApiKeyCreating] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState({});

  // API Log settings state
  const [apiLogs, setApiLogs] = useState({});
  const [apiLogsLoading, setApiLogsLoading] = useState(false);

  // Centralized Booking Control
  const [bookingControl, setBookingControl] = useState(null);
  const [bcSaving, setBcSaving] = useState(false);
  const [bcContactInput, setBcContactInput] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchCashfreeSettings();
    fetchPayAtVenueSetting();
    fetchVenueRecipients();
    fetchVenueList();
    fetchMaksioRecipients();
    fetchWaToggle();
    fetchWaCategoryToggles();
    fetchTemplateNames();
    fetchTemplateConfig();
    fetchApiKeys();
    fetchApiLogs();
    fetchReviewCooldown();
    fetchBookingControl();
    fetchLlmConfig();
  }, []);

  const fetchLlmConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/llm-config`);
      setLlmConfig({ provider: res.data.provider, model: res.data.model, updated_by: res.data.updated_by, updated_at: res.data.updated_at });
      setLlmOptions(res.data.options || {});
    } catch (error) {
      console.error('Failed to fetch LLM config:', error);
    }
  };

  const saveLlmConfig = async (provider, model) => {
    setLlmSaving(true);
    try {
      await axios.put(`${API}/settings/llm-config`, { provider, model });
      setLlmConfig((prev) => ({ ...prev, provider, model }));
      toast.success(`AI model set to ${provider} / ${model}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update AI model');
      fetchLlmConfig();
    } finally {
      setLlmSaving(false);
    }
  };

  const fetchBookingControl = async () => {
    try {
      const response = await axios.get(`${API}/settings/booking-control`);
      const ctrl = response.data.control;
      setBookingControl(ctrl);
      setBcContactInput(ctrl.contact_number || '');
    } catch (error) {
      console.error('Failed to fetch booking control:', error);
    }
  };

  const saveBookingControl = async (next) => {
    setBcSaving(true);
    try {
      await axios.put(`${API}/settings/booking-control`, next);
      setBookingControl(next);
      toast.success('Booking control updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking control');
      fetchBookingControl();
    } finally {
      setBcSaving(false);
    }
  };

  const updateBc = (patch) => {
    if (!bookingControl) return;
    saveBookingControl({ ...bookingControl, ...patch });
  };

  const toggleBcDay = (dayKey) => {
    if (!bookingControl) return;
    const days = { ...bookingControl.days, [dayKey]: !bookingControl.days[dayKey] };
    saveBookingControl({ ...bookingControl, days });
  };

  const toggleBcChannel = (channel) => {
    if (!bookingControl) return;
    const channels = { ...bookingControl.channels, [channel]: !bookingControl.channels[channel] };
    saveBookingControl({ ...bookingControl, channels });
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/otp`);
      setOtpEnabled(response.data.otp_enabled);
      setOtpUpdatedBy(response.data.updated_by);
      setOtpUpdatedAt(response.data.updated_at);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpToggle = async (enabled) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings/otp`, { enabled });
      setOtpEnabled(response.data.otp_enabled);
      toast.success(response.data.message);
      fetchSettings(); // Refresh to get updated_by info
    } catch (error) {
      console.error('Failed to update OTP setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const fetchWaTemplateStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/template-status`);
      setWaTemplate(response.data);
    } catch (error) {
      console.error('Failed to fetch WA template:', error);
    }
  };

  const handleCreateTemplate = async () => {
    setWaLoading(true);
    try {
      const response = await axios.post(`${API}/settings/whatsapp/create-template`);
      if (response.data.success) {
        toast.success('WhatsApp template submitted for approval');
        fetchWaTemplateStatus();
      } else {
        toast.error(response.data.error || 'Failed to create template');
      }
    } catch (error) {
      toast.error('Failed to create template');
    } finally {
      setWaLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    setWaLoading(true);
    try {
      await fetchWaTemplateStatus();
      toast.success('Status refreshed');
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setWaLoading(false);
    }
  };

  const fetchWaReminderStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/reminder-status`);
      setWaReminder(response.data);
    } catch (error) {
      console.error('Failed to fetch WA reminder template:', error);
    }
  };

  const handleCreateReminderTemplate = async () => {
    setWaReminderLoading(true);
    try {
      const response = await axios.post(`${API}/settings/whatsapp/create-reminder-template`);
      if (response.data.success) {
        toast.success('Reminder template submitted for approval');
        fetchWaReminderStatus();
      } else {
        toast.error(response.data.error || 'Failed to create reminder template');
      }
    } catch (error) {
      toast.error('Failed to create reminder template');
    } finally {
      setWaReminderLoading(false);
    }
  };

  const handleRefreshReminderStatus = async () => {
    setWaReminderLoading(true);
    try {
      await fetchWaReminderStatus();
      toast.success('Reminder status refreshed');
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setWaReminderLoading(false);
    }
  };

  const fetchWaReviewStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/review-status`);
      setWaReview(response.data);
    } catch (error) {
      console.error('Failed to fetch WA review templates:', error);
    }
  };

  const handleCreateReviewTemplate = async () => {
    setWaReviewLoading(true);
    try {
      const response = await axios.post(`${API}/settings/whatsapp/create-review-templates`);
      if (response.data.success) {
        toast.success('All 3 venue review templates submitted for approval');
        fetchWaReviewStatus();
      } else {
        toast.error(response.data.error || 'Failed to create review templates');
      }
    } catch (error) {
      toast.error('Failed to create review templates');
    } finally {
      setWaReviewLoading(false);
    }
  };

  const handleRefreshReviewStatus = async () => {
    setWaReviewLoading(true);
    try {
      const response = await axios.get(`${API}/settings/whatsapp/review-status?refresh=true`);
      setWaReview(response.data);
      toast.success('Review statuses refreshed');
    } catch (error) {
      toast.error('Failed to refresh');
    } finally {
      setWaReviewLoading(false);
    }
  };

  const fetchCashfreeSettings = async () => {
    try {
      const response = await axios.get(`${API}/payments/settings`);
      setCashfreeSettings(response.data);
      setCashfreeEnabled(response.data.enabled || false);
    } catch (error) {
      console.error('Failed to fetch Cashfree settings:', error);
    }
  };

  const handleCashfreeToggle = async (checked) => {
    setCashfreeSaving(true);
    try {
      const response = await axios.put(`${API}/payments/settings`, { enabled: checked });
      if (response.data.success) {
        setCashfreeEnabled(checked);
        toast.success(`Payment gateway ${checked ? 'enabled' : 'disabled'}`);
        fetchCashfreeSettings();
      }
    } catch (error) {
      toast.error('Failed to update payment settings');
    } finally {
      setCashfreeSaving(false);
    }
  };

  const fetchPayAtVenueSetting = async () => {
    try {
      const response = await axios.get(`${API}/payments/pay-at-venue`);
      setPayAtVenueEnabled(response.data.enabled ?? true);
      setPayAtVenueMeta({ updated_by: response.data.updated_by, updated_at: response.data.updated_at });
    } catch (error) {
      console.error('Failed to fetch Pay at Venue setting:', error);
    }
  };

  const handlePayAtVenueToggle = async (checked) => {
    setPayAtVenueSaving(true);
    try {
      const response = await axios.put(`${API}/payments/pay-at-venue`, { enabled: checked });
      if (response.data.success) {
        setPayAtVenueEnabled(checked);
        toast.success(`Pay at Venue ${checked ? 'enabled' : 'disabled'}`);
        fetchPayAtVenueSetting();
      }
    } catch (error) {
      toast.error('Failed to update Pay at Venue setting');
    } finally {
      setPayAtVenueSaving(false);
    }
  };

  const fetchVenueList = async () => {
    try {
      const response = await axios.get(`${API}/masters/venues`);
      setVenueList(response.data.venues || []);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
    }
  };

  const fetchVenueRecipients = async () => {
    try {
      const response = await axios.get(`${API}/settings/venue-notifications`);
      setVenueRecipients(response.data.recipients || []);
    } catch (error) {
      console.error('Failed to fetch venue recipients:', error);
    }
  };

  const handleVnSave = async () => {
    if (!vnForm.name.trim() || !vnForm.mobile.trim() || vnForm.venues.length === 0) {
      toast.error('Name, mobile, and at least one venue are required');
      return;
    }
    setVnLoading(true);
    try {
      if (vnEditing) {
        await axios.put(`${API}/settings/venue-notifications/${vnEditing}`, vnForm);
        toast.success('Recipient updated');
      } else {
        await axios.post(`${API}/settings/venue-notifications`, vnForm);
        toast.success('Recipient added');
      }
      setVnForm({ name: '', mobile: '', venues: [] });
      setVnEditing(null);
      setVnShowForm(false);
      fetchVenueRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipient');
    } finally {
      setVnLoading(false);
    }
  };

  const handleVnDelete = async (id) => {
    if (!window.confirm('Remove this notification recipient?')) return;
    try {
      await axios.delete(`${API}/settings/venue-notifications/${id}`);
      toast.success('Recipient removed');
      fetchVenueRecipients();
    } catch (error) {
      toast.error('Failed to delete recipient');
    }
  };

  const handleVnEdit = (recipient) => {
    setVnForm({ name: recipient.name, mobile: recipient.mobile, venues: recipient.venues });
    setVnEditing(recipient.id);
    setVnShowForm(true);
  };

  const toggleVnVenue = (venueName) => {
    setVnForm(prev => ({
      ...prev,
      venues: prev.venues.includes(venueName)
        ? prev.venues.filter(v => v !== venueName)
        : [...prev.venues, venueName]
    }));
  };

  const fetchMaksioRecipients = async () => {
    try {
      const response = await axios.get(`${API}/webhook/whatsapp/maksio-notify-recipients`);
      setMaksioRecipients(response.data.recipients || []);
    } catch (error) {
      console.error('Failed to fetch MAKSIO notify recipients:', error);
    }
  };

  const handleMaksioSave = async () => {
    if (!maksioForm.name.trim() || !maksioForm.mobile.trim()) {
      toast.error('Name and mobile are required');
      return;
    }
    setMaksioLoading(true);
    try {
      if (maksioEditing) {
        await axios.put(`${API}/webhook/whatsapp/maksio-notify-recipients/${maksioEditing}`, maksioForm);
        toast.success('Recipient updated');
      } else {
        await axios.post(`${API}/webhook/whatsapp/maksio-notify-recipients`, maksioForm);
        toast.success('Recipient added');
      }
      setMaksioForm({ name: '', mobile: '', is_active: true });
      setMaksioEditing(null);
      setMaksioShowForm(false);
      fetchMaksioRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipient');
    } finally {
      setMaksioLoading(false);
    }
  };

  const handleMaksioDelete = async (id) => {
    if (!window.confirm('Remove this notification recipient?')) return;
    try {
      await axios.delete(`${API}/webhook/whatsapp/maksio-notify-recipients/${id}`);
      toast.success('Recipient removed');
      fetchMaksioRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete recipient');
    }
  };

  const handleMaksioEdit = (recipient) => {
    setMaksioForm({ name: recipient.name, mobile: recipient.mobile, is_active: recipient.is_active });
    setMaksioEditing(recipient.id);
    setMaksioShowForm(true);
  };

  const toggleMaksioActive = async (r) => {
    try {
      await axios.put(`${API}/webhook/whatsapp/maksio-notify-recipients/${r.id}`, { ...r, is_active: !r.is_active });
      fetchMaksioRecipients();
    } catch (error) {
      toast.error('Failed to toggle recipient');
    }
  };


  const fetchWaToggle = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/toggle`);
      setWaEnabled(response.data.enabled || false);
      setWaToggleMeta({ updated_by: response.data.updated_by, updated_at: response.data.updated_at });
    } catch (error) {
      console.error('Failed to fetch WA toggle:', error);
    }
  };

  const handleWaToggle = async (checked) => {
    setWaToggleLoading(true);
    try {
      const response = await axios.put(`${API}/settings/whatsapp/toggle`, { enabled: checked });
      if (response.data.success) {
        setWaEnabled(checked);
        toast.success(response.data.message);
        fetchWaToggle();
      }
    } catch (error) {
      toast.error('Failed to update WhatsApp setting');
    } finally {
      setWaToggleLoading(false);
    }
  };

  const fetchWaCategoryToggles = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/category-toggles`);
      if (response.data?.toggles) {
        setWaCategoryToggles((prev) => ({ ...prev, ...response.data.toggles }));
      }
    } catch (error) {
      console.error('Failed to fetch WA category toggles:', error);
    }
  };

  const handleWaCategoryToggle = async (key, checked) => {
    const next = { ...waCategoryToggles, [key]: checked };
    // Optimistic update
    setWaCategoryToggles(next);
    setWaCategoryLoading(key);
    try {
      const response = await axios.put(`${API}/settings/whatsapp/category-toggles`, { toggles: next });
      if (response.data?.success) {
        toast.success(`${checked ? 'Enabled' : 'Disabled'} ${WA_CATEGORY_META[key]?.label || key}`);
      }
    } catch (error) {
      // Roll back
      setWaCategoryToggles((prev) => ({ ...prev, [key]: !checked }));
      toast.error('Failed to update category toggle');
    } finally {
      setWaCategoryLoading(null);
    }
  };

  const fetchReviewCooldown = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/review-cooldown`);
      const days = Number(response.data?.cooldown_days ?? 30);
      setReviewCooldownDays(days);
      setReviewCooldownInput(String(days));
    } catch (error) {
      console.error('Failed to fetch review cooldown:', error);
    }
  };

  const handleSaveReviewCooldown = async () => {
    const n = parseInt(reviewCooldownInput, 10);
    if (Number.isNaN(n) || n < 0 || n > 365) {
      toast.error('Cooldown must be a whole number between 0 and 365');
      return;
    }
    setReviewCooldownSaving(true);
    try {
      const response = await axios.put(`${API}/settings/whatsapp/review-cooldown`, {
        cooldown_days: n,
      });
      if (response.data?.success) {
        setReviewCooldownDays(n);
        toast.success(
          n === 0
            ? 'Cooldown disabled — every session will trigger a review'
            : `Review cooldown set to ${n} day${n === 1 ? '' : 's'}`,
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update review cooldown');
    } finally {
      setReviewCooldownSaving(false);
    }
  };

  const fetchTemplateNames = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/template-names`);
      if (response.data.success) {
        setTemplateNames(response.data.names);
      }
    } catch (error) {
      console.error('Failed to fetch template names:', error);
    }
  };

  const handleSaveTemplateNames = async () => {
    setTplSaving(true);
    try {
      const response = await axios.put(`${API}/settings/whatsapp/template-names`, { names: templateNames });
      if (response.data.success) {
        toast.success('Template names updated');
        setTplEditing(false);
      }
    } catch (error) {
      toast.error('Failed to save template names');
    } finally {
      setTplSaving(false);
    }
  };

  const fetchTemplateConfig = async () => {
    try {
      const response = await axios.get(`${API}/settings/whatsapp/template-config`);
      if (response.data.success) {
        setTemplateConfig(response.data.config || {});
      }
    } catch (error) {
      console.error('Failed to fetch template config:', error);
    }
  };

  const updateTplCfg = (key, field, value) => {
    setTemplateConfig((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const toggleTplParam = (key, param) => {
    setTemplateConfig((prev) => {
      const entry = prev[key] || {};
      const params = entry.params || [];
      const next = params.includes(param)
        ? params.filter((p) => p !== param)
        : [...params, param];
      return { ...prev, [key]: { ...entry, params: next } };
    });
  };

  const handleSaveTemplateConfig = async () => {
    setTplSaving(true);
    try {
      const response = await axios.put(`${API}/settings/whatsapp/template-config`, { config: templateConfig });
      if (response.data.success) {
        toast.success('Template configuration updated');
        setTplEditing(false);
        fetchTemplateNames();
        fetchTemplateConfig();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template configuration');
    } finally {
      setTplSaving(false);
    }
  };

  // ========== API KEY MANAGEMENT ==========

  const fetchApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const response = await axios.get(`${API}/settings/api-keys`);
      setApiKeys(response.data.keys || []);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!apiKeyForm.partner_name.trim()) {
      toast.error('Partner/Platform name is required');
      return;
    }
    setApiKeyCreating(true);
    try {
      const response = await axios.post(`${API}/settings/api-keys`, apiKeyForm);
      if (response.data.success) {
        toast.success(`API key created for ${apiKeyForm.partner_name}`);
        setApiKeyForm({ partner_name: '' });
        setApiKeyShowForm(false);
        fetchApiKeys();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setApiKeyCreating(false);
    }
  };

  const handleToggleApiKey = async (keyId, currentActive) => {
    try {
      await axios.put(`${API}/settings/api-keys/${keyId}`, { is_active: !currentActive });
      toast.success(`API key ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchApiKeys();
    } catch (error) {
      toast.error('Failed to update API key');
    }
  };

  const handleDeleteApiKey = async (keyId, partnerName) => {
    if (!window.confirm(`Delete API key for "${partnerName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/settings/api-keys/${keyId}`);
      toast.success('API key deleted');
      fetchApiKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete API key');
    }
  };

  const handleRegenerateApiKey = async (keyId, partnerName) => {
    if (!window.confirm(`Regenerate API key for "${partnerName}"? The old key will stop working immediately.`)) return;
    try {
      const response = await axios.post(`${API}/settings/api-keys/${keyId}/regenerate`);
      if (response.data.success) {
        toast.success('API key regenerated');
        fetchApiKeys();
      }
    } catch (error) {
      toast.error('Failed to regenerate API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleKeyVisibility = (keyId) => {
    setApiKeyVisible(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const maskApiKey = (key) => {
    if (!key) return '';
    return key.substring(0, 8) + '••••••••••••' + key.substring(key.length - 4);
  };

  // ========== API LOG SETTINGS ==========

  const fetchApiLogs = async () => {
    setApiLogsLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings/api-logs`);
      setApiLogs(data.logs || {});
    } catch (err) {
      console.error('Failed to fetch API log settings:', err);
    } finally {
      setApiLogsLoading(false);
    }
  };

  const handleToggleApiLog = async (logKey) => {
    const updated = { ...apiLogs };
    updated[logKey] = { ...updated[logKey], enabled: !updated[logKey].enabled };
    setApiLogs(updated);
    try {
      await axios.put(`${API}/settings/api-logs`, { logs: updated });
      toast.success(`${updated[logKey].label} ${updated[logKey].enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update log settings');
      fetchApiLogs();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight flex items-center gap-3">
            <Settings className="w-8 h-8 text-primary" />
            Settings
          </h1>
          <p className="text-slate-600 mt-1">
            Configure system-wide settings for the MAKS Booking System
          </p>
        </div>

        {/* Centralized Booking Control */}
        <Card className="p-6" data-testid="booking-control-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-violet-100">
              <ToggleLeft className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Booking Control</h2>
              <p className="text-sm text-slate-500">Enable or disable customer online bookings by channel and by day of the week. Applies to the date of the slot being booked. Admin-panel bookings are never affected.</p>
            </div>
          </div>

          {!bookingControl ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading booking control…
            </div>
          ) : (
            <div className="space-y-4">
              {/* Master toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <Label className="text-base font-medium text-slate-900">Master Online Booking</Label>
                  <p className="text-sm text-slate-500 mt-0.5">Turn this OFF to instantly disable all customer online bookings (App & WhatsApp).</p>
                </div>
                <Switch
                  checked={bookingControl.master_enabled}
                  onCheckedChange={() => updateBc({ master_enabled: !bookingControl.master_enabled })}
                  disabled={bcSaving}
                  data-testid="bc-master-toggle"
                />
              </div>

              {/* Channels */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className={`flex items-center justify-between p-4 rounded-xl border ${bookingControl.master_enabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-violet-600" />
                    <Label className="text-sm font-medium text-slate-900">App / Web</Label>
                  </div>
                  <Switch
                    checked={bookingControl.channels?.app}
                    onCheckedChange={() => toggleBcChannel('app')}
                    disabled={bcSaving || !bookingControl.master_enabled}
                    data-testid="bc-channel-app"
                  />
                </div>
                <div className={`flex items-center justify-between p-4 rounded-xl border ${bookingControl.master_enabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-emerald-600" />
                    <Label className="text-sm font-medium text-slate-900">WhatsApp (Makiso)</Label>
                  </div>
                  <Switch
                    checked={bookingControl.channels?.whatsapp}
                    onCheckedChange={() => toggleBcChannel('whatsapp')}
                    disabled={bcSaving || !bookingControl.master_enabled}
                    data-testid="bc-channel-whatsapp"
                  />
                </div>
              </div>

              {/* Per-weekday */}
              <div className={`p-4 rounded-xl border border-slate-200 ${bookingControl.master_enabled ? '' : 'opacity-60'}`}>
                <Label className="text-sm font-medium text-slate-900">Open Days (by slot date)</Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-3">Toggle a day OFF to block customer bookings for any slot falling on that weekday.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { key: 'mon', label: 'Monday' },
                    { key: 'tue', label: 'Tuesday' },
                    { key: 'wed', label: 'Wednesday' },
                    { key: 'thu', label: 'Thursday' },
                    { key: 'fri', label: 'Friday' },
                    { key: 'sat', label: 'Saturday' },
                    { key: 'sun', label: 'Sunday' },
                  ].map((d) => {
                    const open = bookingControl.days?.[d.key];
                    return (
                      <button
                        key={d.key}
                        onClick={() => toggleBcDay(d.key)}
                        disabled={bcSaving || !bookingControl.master_enabled}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                          open
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                        }`}
                        data-testid={`bc-day-${d.key}`}
                      >
                        <span>{d.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${open ? 'bg-emerald-200/60' : 'bg-red-200/60'}`}>
                          {open ? 'Open' : 'Closed'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contact number */}
              <div className="p-4 rounded-xl border border-slate-200">
                <Label className="text-sm font-medium text-slate-900">Fallback Contact Number</Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-2">Shown to customers when bookings are closed ("Please call …").</p>
                <div className="flex gap-2">
                  <Input
                    value={bcContactInput}
                    onChange={(e) => setBcContactInput(e.target.value)}
                    placeholder="9429694401"
                    className="flex-1"
                    data-testid="bc-contact-input"
                  />
                  <Button
                    onClick={() => updateBc({ contact_number: bcContactInput.trim() })}
                    disabled={bcSaving || !bcContactInput.trim() || bcContactInput.trim() === bookingControl.contact_number}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="bc-contact-save"
                  >
                    {bcSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>

              {/* Status */}
              <div className={`p-3 rounded-lg border ${bookingControl.master_enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <p className={`text-sm font-medium ${bookingControl.master_enabled ? 'text-emerald-700' : 'text-red-700'}`}>
                  {bookingControl.master_enabled
                    ? '✓ Online booking is ON. Customers can book on open days via enabled channels.'
                    : '⚠ Online booking is OFF. All customer App & WhatsApp bookings are blocked.'}
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* AI Model (LLM) Configuration */}
        <Card className="p-6" data-testid="llm-config-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-indigo-100">
              <MessageSquare className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-xl text-slate-900">AI Model Configuration</h2>
              <p className="text-slate-600 text-sm mt-1">
                Choose the AI provider &amp; model powering booking parsing, WhatsApp chat, and email parsing.
              </p>
            </div>
          </div>
          {llmConfig && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Provider</label>
                  <select
                    data-testid="llm-provider-select"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    value={llmConfig.provider}
                    disabled={llmSaving}
                    onChange={(e) => {
                      const provider = e.target.value;
                      const model = (llmOptions[provider] || [])[0];
                      saveLlmConfig(provider, model);
                    }}
                  >
                    {Object.keys(llmOptions).map((p) => (
                      <option key={p} value={p}>{p === 'openai' ? 'OpenAI' : p === 'gemini' ? 'Google Gemini' : p === 'anthropic' ? 'Anthropic Claude' : p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Model</label>
                  <select
                    data-testid="llm-model-select"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    value={llmConfig.model}
                    disabled={llmSaving}
                    onChange={(e) => saveLlmConfig(llmConfig.provider, e.target.value)}
                  >
                    {(llmOptions[llmConfig.provider] || []).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="p-3 rounded-lg border bg-indigo-50 border-indigo-200">
                <p className="text-sm font-medium text-indigo-700" data-testid="llm-config-status">
                  Active: <span className="font-semibold">{llmConfig.provider}</span> / {llmConfig.model}
                  {llmSaving && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
                </p>
                <p className="text-xs text-indigo-600 mt-1">
                  Changes apply to all AI features within ~30 seconds. (Powered by the Emergent LLM key.)
                </p>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-blue-100">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Authentication</h2>
              <p className="text-sm text-slate-500">Configure login and verification settings</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* OTP Login Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-white shadow-sm">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <Label className="text-base font-medium text-slate-900">
                    OTP Verification for Customer Login
                  </Label>
                  <p className="text-sm text-slate-500 mt-0.5">
                    When enabled, customers must verify their mobile number with a 4-digit OTP before logging in
                  </p>
                  {otpUpdatedBy && (
                    <p className="text-xs text-slate-400 mt-2">
                      Last updated by {otpUpdatedBy} on {formatDate(otpUpdatedAt)}
                    </p>
                  )}
                </div>
              </div>
              <Switch
                checked={otpEnabled}
                onCheckedChange={handleOtpToggle}
                disabled={saving}
                data-testid="otp-toggle"
              />
            </div>

            {/* Status Indicator */}
            <div className={`p-3 rounded-lg border ${otpEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-sm font-medium ${otpEnabled ? 'text-emerald-700' : 'text-amber-700'}`}>
                {otpEnabled 
                  ? '✓ OTP verification is ENABLED. Customers will receive a 4-digit SMS code on login.'
                  : '⚠ OTP verification is DISABLED. Customers can login directly with their mobile number.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">About OTP Verification</p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>OTP is sent via SMS gateway</li>
                <li>4-digit code valid for 5 minutes</li>
                <li>Helps verify genuine customers and prevent fake bookings</li>
                <li>Each SMS costs approximately ₹0.15-0.20</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Cashfree Payment Gateway */}
        <Card className="p-6" data-testid="cashfree-settings-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-indigo-100">
              <CreditCard className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">Cashfree Payment Gateway</h2>
              <p className="text-sm text-slate-500">Accept online payments from players on the booking portal</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <Label htmlFor="cashfree-toggle" className="font-medium text-slate-900">
                  Online Payments
                </Label>
                <p className="text-sm text-slate-500 mt-0.5">
                  {cashfreeEnabled ? 'Players can pay online when booking' : 'Online payment is turned off'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {cashfreeSaving && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
                <Switch
                  id="cashfree-toggle"
                  checked={cashfreeEnabled}
                  onCheckedChange={handleCashfreeToggle}
                  disabled={cashfreeSaving}
                  data-testid="cashfree-toggle"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Environment</p>
                <p className="text-sm font-semibold text-slate-900">{cashfreeSettings?.environment || 'PRODUCTION'}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">API Keys</p>
                <p className={`text-sm font-semibold ${cashfreeSettings?.client_id_configured ? 'text-green-600' : 'text-red-600'}`}>
                  {cashfreeSettings?.client_id_configured ? 'Configured' : 'Not Set'}
                </p>
              </div>
            </div>

            {cashfreeSettings?.updated_by && (
              <p className="text-xs text-slate-400">
                Last updated by {cashfreeSettings.updated_by} on {formatDate(cashfreeSettings.updated_at)}
              </p>
            )}

            <div className={`p-3 rounded-lg border ${cashfreeEnabled ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
              <p className={`text-sm font-medium ${cashfreeEnabled ? 'text-green-700' : 'text-slate-600'}`}>
                {cashfreeEnabled
                  ? 'Payment gateway is ACTIVE. Players will see the "Pay Online" option when booking.'
                  : 'Payment gateway is OFF. Players can only book without online payment.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Pay at Venue Toggle */}
        <Card className="p-6" data-testid="pay-at-venue-settings-card">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Banknote className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Pay at Venue</h2>
              <p className="text-sm text-slate-500">Allow players to choose "Pay at Venue" when booking slots</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="pay-at-venue-toggle" className="font-medium text-slate-900">
                  {payAtVenueEnabled ? 'Players can choose to pay at venue' : 'Pay at Venue option is hidden'}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                {payAtVenueSaving && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
                <Switch
                  id="pay-at-venue-toggle"
                  checked={payAtVenueEnabled}
                  onCheckedChange={handlePayAtVenueToggle}
                  disabled={payAtVenueSaving}
                  data-testid="pay-at-venue-toggle"
                />
              </div>
            </div>

            {payAtVenueMeta?.updated_by && (
              <p className="text-xs text-slate-400">
                Last updated by {payAtVenueMeta.updated_by} on {formatDate(payAtVenueMeta.updated_at)}
              </p>
            )}

            <div className={`p-3 rounded-lg border ${payAtVenueEnabled ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
              <p className={`text-sm font-medium ${payAtVenueEnabled ? 'text-amber-700' : 'text-slate-600'}`}>
                {payAtVenueEnabled
                  ? 'Players will see both "Pay Online" and "Pay at Venue" options when booking.'
                  : 'Players will only see the "Pay Online" option. Pay at Venue is disabled.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Public API Keys Management */}
        <Card className="p-6" data-testid="api-keys-settings-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-slate-800">
              <Key className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Public API Keys</h2>
                  <p className="text-sm text-slate-500">Manage API keys for partner platforms (Khelomore, Hudle, District, etc.)</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => { setApiKeyForm({ partner_name: '' }); setApiKeyShowForm(!apiKeyShowForm); }}
                  className="bg-slate-800 hover:bg-slate-900"
                  data-testid="add-api-key-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Generate Key
                </Button>
              </div>
            </div>
          </div>

          {/* Create Form */}
          {apiKeyShowForm && (
            <div className="mb-5 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3" data-testid="api-key-create-form">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Platform / Partner Name</label>
                <Input
                  value={apiKeyForm.partner_name}
                  onChange={e => setApiKeyForm({ partner_name: e.target.value })}
                  placeholder="e.g. Khelomore, Hudle, District, Custom App"
                  data-testid="api-key-partner-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleCreateApiKey}
                  disabled={apiKeyCreating}
                  className="bg-slate-800 hover:bg-slate-900"
                  data-testid="api-key-create-btn"
                >
                  {apiKeyCreating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Key className="w-4 h-4 mr-1" />}
                  Generate API Key
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setApiKeyShowForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Keys List */}
          {apiKeysLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No API keys created yet. Generate one for partner integrations.
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map(k => (
                <div key={k.id} className={`p-4 rounded-xl border transition-colors ${k.is_active ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200 opacity-70'}`} data-testid={`api-key-row-${k.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        k.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {k.role === 'admin' ? 'Admin' : 'Partner'}
                      </span>
                      <h3 className="font-semibold text-slate-900 text-sm">{k.partner_name}</h3>
                      {!k.is_active && <span className="text-xs text-red-600 font-medium">INACTIVE</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleApiKey(k.id, k.is_active)}
                        className={`p-1.5 rounded-lg transition-colors ${k.is_active ? 'hover:bg-amber-100 text-amber-600' : 'hover:bg-green-100 text-green-600'}`}
                        title={k.is_active ? 'Deactivate' : 'Activate'}
                        data-testid={`api-key-toggle-${k.id}`}
                      >
                        <ToggleLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRegenerateApiKey(k.id, k.partner_name)}
                        className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors"
                        title="Regenerate Key"
                        data-testid={`api-key-regen-${k.id}`}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      {k.role !== 'admin' && (
                        <button
                          onClick={() => handleDeleteApiKey(k.id, k.partner_name)}
                          className="p-1.5 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
                          title="Delete Key"
                          data-testid={`api-key-delete-${k.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Key Display */}
                  <div className="flex items-center gap-2 mb-2">
                    <code className="flex-1 text-xs bg-slate-100 px-3 py-2 rounded-lg font-mono text-slate-700 select-all">
                      {apiKeyVisible[k.id] ? k.key : maskApiKey(k.key)}
                    </code>
                    <button
                      onClick={() => toggleKeyVisibility(k.id)}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                      title={apiKeyVisible[k.id] ? 'Hide' : 'Show'}
                      data-testid={`api-key-visibility-${k.id}`}
                    >
                      {apiKeyVisible[k.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(k.key)}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                      title="Copy to clipboard"
                      data-testid={`api-key-copy-${k.id}`}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span>Used: {k.usage_count || 0} times</span>
                    {k.last_used && <span>Last: {formatDate(k.last_used)}</span>}
                    {k.created_by && <span>By: {k.created_by}</span>}
                    <span>Created: {formatDate(k.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info */}
          <div className="mt-4 p-3 rounded-lg border bg-slate-50 border-slate-200">
            <p className="text-sm text-slate-600">
              <strong>API Base URL:</strong> <code className="bg-slate-200 px-1.5 py-0.5 rounded text-xs">{BACKEND_URL}/api/v1</code>
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Partners use the <code className="bg-slate-200 px-1 rounded">ApiKey</code> header to access venues, slots, booking, and cancellation endpoints. Each platform should have its own key for tracking.
            </p>
          </div>
        </Card>

        {/* API & Integration Logs */}
        <Card className="p-6" data-testid="api-logs-settings-card">
          <div className="flex items-start gap-4 mb-5">
            <div className="p-3 rounded-xl bg-indigo-50">
              <Shield className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">API & Integration Logs</h2>
              <p className="text-sm text-slate-500">Enable/disable logging for integrations and view their dashboards</p>
            </div>
          </div>

          {apiLogsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(apiLogs).map(([logKey, logData]) => (
                <div key={logKey} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors" data-testid={`api-log-row-${logKey}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${logData.enabled ? 'bg-green-500' : 'bg-slate-300'}`} />
                    <div>
                      <span className="text-sm font-medium text-slate-900">{logData.label}</span>
                      <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${logData.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {logData.enabled ? 'ON' : 'OFF'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {logData.path && (
                      <a
                        href={logData.path}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        data-testid={`api-log-link-${logKey}`}
                      >
                        View Dashboard
                      </a>
                    )}
                    <button
                      onClick={() => handleToggleApiLog(logKey)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${logData.enabled ? 'bg-green-500' : 'bg-slate-300'}`}
                      data-testid={`api-log-toggle-${logKey}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${logData.enabled ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Email Forwarding Webhook */}
        <Card className="p-6" data-testid="email-forwarding-card">
          <div className="flex items-start gap-4 mb-5">
            <div className="p-3 rounded-xl bg-blue-50">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Email Forwarding</h2>
              <p className="text-sm text-slate-500">
                Forward booking emails (Khelomore, District, Hudle, etc.) to this webhook URL for
                automatic parsing and booking creation.
              </p>
            </div>
          </div>
          <div className="space-y-3 pl-1">
            <div className="flex gap-2 items-center">
              <code
                className="flex-1 px-3 py-2 bg-slate-50 rounded border border-slate-200 text-sm font-mono text-slate-900 overflow-x-auto"
                data-testid="email-webhook-url"
              >
                {BACKEND_URL}/api/email-webhook
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`${BACKEND_URL}/api/email-webhook`);
                  toast.success('Webhook URL copied!');
                }}
                data-testid="copy-email-webhook-btn"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
            </div>
            <div className="text-sm text-slate-600 space-y-2 bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="font-medium text-slate-800">Quick setup:</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-600">
                <li>In your email client (Gmail / Outlook), set up a filter for booking emails.</li>
                <li>Forward matching emails to a service like <em>Zapier</em> / <em>n8n</em> / <em>Make</em> that POSTs to the webhook URL above.</li>
                <li>Payload format: <code className="text-xs bg-white px-1 py-0.5 rounded">{`{ "from": "...", "subject": "...", "body": "..." }`}</code></li>
                <li>Parsed bookings appear under <strong>Email Bookings</strong> with a health badge for review.</li>
              </ol>
              <a
                href="/EMAIL_FORWARDING_SETUP.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium inline-block pt-1"
                data-testid="email-forwarding-docs-link"
              >
                Full setup instructions →
              </a>
            </div>
          </div>
        </Card>

        {/* WhatsApp Notifications Master Toggle */}
        <Card className="p-6" data-testid="whatsapp-toggle-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-green-100">
              <MessageSquare className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">WhatsApp Notifications</h2>
              <p className="text-sm text-slate-500">Master switch for all WhatsApp messages — confirmations, reminders & reviews</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <Label htmlFor="wa-toggle" className="font-medium text-slate-900">
                  Enable WhatsApp Notifications
                </Label>
                <p className="text-sm text-slate-500 mt-0.5">
                  {waEnabled ? 'Players and venue recipients will receive WhatsApp messages' : 'All WhatsApp messages are paused'}
                </p>
                {waToggleMeta?.updated_by && (
                  <p className="text-xs text-slate-400 mt-2">
                    Last updated by {waToggleMeta.updated_by} on {formatDate(waToggleMeta.updated_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {waToggleLoading && <Loader2 className="w-4 h-4 animate-spin text-green-500" />}
                <Switch
                  id="wa-toggle"
                  checked={waEnabled}
                  onCheckedChange={handleWaToggle}
                  disabled={waToggleLoading}
                  data-testid="whatsapp-master-toggle"
                />
              </div>
            </div>

            <div className={`p-3 rounded-lg border ${waEnabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm font-medium ${waEnabled ? 'text-green-700' : 'text-red-700'}`}>
                {waEnabled
                  ? 'WhatsApp is ON. Booking confirmations, 1-hour reminders, and post-game reviews will be sent automatically.'
                  : 'WhatsApp is OFF. No messages will be sent to players or venue recipients until this is enabled.'}
              </p>
            </div>

            {/* Per-category (per-template) toggles */}
            <div
              className={`pt-2 ${!waEnabled ? 'opacity-50 pointer-events-none' : ''}`}
              data-testid="wa-category-toggles"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-900">
                  Per-message Controls
                </h3>
                <span className="text-xs text-slate-500">
                  Master switch must be ON for these to apply
                </span>
              </div>
              <div className="grid gap-2">
                {Object.entries(WA_CATEGORY_META).map(([key, meta]) => {
                  const tplName = templateNames[key] || '—';
                  const checked = !!waCategoryToggles[key];
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition"
                      data-testid={`wa-cat-row-${key}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Label
                            htmlFor={`wa-cat-${key}`}
                            className="font-medium text-slate-900 cursor-pointer"
                          >
                            {meta.label}
                          </Label>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${
                              checked
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border-slate-200'
                            }`}
                          >
                            {checked ? 'ENABLED' : 'PAUSED'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {meta.description}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">
                            Template
                          </span>
                          <code
                            className="text-xs font-mono text-primary bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200"
                            data-testid={`wa-cat-template-${key}`}
                          >
                            {tplName}
                          </code>
                          <span className="text-[10px] text-slate-400">
                            • {meta.trigger}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {waCategoryLoading === key && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-green-500" />
                        )}
                        <Switch
                          id={`wa-cat-${key}`}
                          checked={checked}
                          onCheckedChange={(v) => handleWaCategoryToggle(key, v)}
                          disabled={!waEnabled || waCategoryLoading === key}
                          data-testid={`wa-cat-toggle-${key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>

        {/* Review Cooldown */}
        <Card className="p-6" data-testid="review-cooldown-card">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <MessageCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">Review Message Cooldown</h2>
              <p className="text-sm text-slate-500">
                Skip the post-game review WhatsApp if the same contact already received one within this window.
                A multi-slot booking always counts as a <span className="font-medium">single session</span> — only
                one review goes out per session.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <Label htmlFor="review-cooldown-input" className="text-xs font-medium text-slate-700 mb-1.5 block">
                Cooldown (days)
              </Label>
              <input
                id="review-cooldown-input"
                type="number"
                min="0"
                max="365"
                step="1"
                value={reviewCooldownInput}
                onChange={(e) => setReviewCooldownInput(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-200 text-sm"
                data-testid="review-cooldown-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Current: <span className="font-semibold text-slate-700">{reviewCooldownDays} day{reviewCooldownDays === 1 ? '' : 's'}</span>
                {reviewCooldownDays === 0 && ' — cooldown disabled'}
              </p>
            </div>
            <Button
              onClick={handleSaveReviewCooldown}
              disabled={reviewCooldownSaving || String(reviewCooldownDays) === reviewCooldownInput}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="review-cooldown-save-btn"
            >
              {reviewCooldownSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save
            </Button>
          </div>
        </Card>

        {/* Venue Notification Recipients */}
        <Card className="p-6" data-testid="venue-notifications-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-violet-100">
              <Users className="w-6 h-6 text-violet-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">Venue Booking Notifications</h2>
              <p className="text-sm text-slate-500">People who receive WhatsApp booking confirmations for specific venues</p>
            </div>
            <Button
              size="sm"
              onClick={() => { setVnForm({ name: '', mobile: '', venues: [] }); setVnEditing(null); setVnShowForm(!vnShowForm); }}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="add-venue-recipient-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Recipient
            </Button>
          </div>

          {/* Add/Edit Form */}
          {vnShowForm && (
            <div className="mb-5 p-4 bg-violet-50 rounded-xl border border-violet-200 space-y-3" data-testid="venue-recipient-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Name</label>
                  <input
                    type="text"
                    value={vnForm.name}
                    onChange={e => setVnForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Alwyn"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    data-testid="vn-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Mobile Number</label>
                  <input
                    type="text"
                    value={vnForm.mobile}
                    onChange={e => setVnForm(p => ({ ...p, mobile: e.target.value }))}
                    placeholder="e.g. 8898876991"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    data-testid="vn-mobile-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-2 block">Mapped Venues (select one or more)</label>
                <div className="flex flex-wrap gap-2">
                  {venueList.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => toggleVnVenue(v.name)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        vnForm.venues.includes(v.name)
                          ? 'bg-violet-600 text-white'
                          : 'bg-white border border-slate-300 text-slate-600 hover:border-violet-400'
                      }`}
                      data-testid={`vn-venue-chip-${v.id}`}
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handleVnSave}
                  disabled={vnLoading}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="vn-save-btn"
                >
                  {vnLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {vnEditing ? 'Update' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setVnShowForm(false); setVnEditing(null); }}
                  data-testid="vn-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Recipients List */}
          {venueRecipients.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No notification recipients configured yet. Add one to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {venueRecipients.map(r => (
                <div key={r.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-testid={`vn-recipient-${r.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                      <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">{r.mobile}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.venues.map(v => (
                        <span key={v} className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-3">
                    <button
                      onClick={() => handleVnEdit(r)}
                      className="p-2 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors"
                      data-testid={`vn-edit-${r.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleVnDelete(r.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
                      data-testid={`vn-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info */}
          <div className="mt-4 p-3 rounded-lg border bg-violet-50 border-violet-200">
            <p className="text-sm text-violet-700">
              When a player books at a venue, these recipients will also receive a WhatsApp booking confirmation. One person can be mapped to multiple venues.
            </p>
          </div>
        </Card>

        {/* MAKSIO New-Conversation Notify Recipients */}
        <Card className="p-6" data-testid="maksio-notifications-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-teal-100">
              <Bot className="w-6 h-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">MAKSIO New-Conversation Alerts</h2>
              <p className="text-sm text-slate-500">People notified via WhatsApp whenever a brand-new MAKSIO chat starts with a player</p>
            </div>
            <Button
              size="sm"
              onClick={() => { setMaksioForm({ name: '', mobile: '', is_active: true }); setMaksioEditing(null); setMaksioShowForm(!maksioShowForm); }}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="add-maksio-recipient-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Recipient
            </Button>
          </div>

          {maksioShowForm && (
            <div className="mb-5 p-4 bg-teal-50 rounded-xl border border-teal-200 space-y-3" data-testid="maksio-recipient-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Name</label>
                  <input
                    type="text"
                    value={maksioForm.name}
                    onChange={e => setMaksioForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Alwyn"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                    data-testid="maksio-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1 block">Mobile Number</label>
                  <input
                    type="text"
                    value={maksioForm.mobile}
                    onChange={e => setMaksioForm(p => ({ ...p, mobile: e.target.value }))}
                    placeholder="e.g. 8898876991"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                    data-testid="maksio-mobile-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handleMaksioSave}
                  disabled={maksioLoading}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="maksio-save-btn"
                >
                  {maksioLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {maksioEditing ? 'Update' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setMaksioShowForm(false); setMaksioEditing(null); }}
                  data-testid="maksio-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {maksioRecipients.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No notification recipients configured yet. Add one to get started.
            </div>
          ) : (
            <div className="space-y-3">
              {maksioRecipients.map(r => (
                <div key={r.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-testid={`maksio-recipient-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Switch checked={r.is_active} onCheckedChange={() => toggleMaksioActive(r)} data-testid={`maksio-recipient-active-${r.id}`} />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                      <span className="text-xs text-slate-500">{r.mobile}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-3">
                    <button
                      onClick={() => handleMaksioEdit(r)}
                      className="p-2 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors"
                      data-testid={`maksio-edit-${r.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMaksioDelete(r.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition-colors"
                      data-testid={`maksio-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 p-3 rounded-lg border bg-teal-50 border-teal-200">
            <p className="text-sm text-teal-700">
              Sent once per new MAKSIO WhatsApp conversation (not repeated for every message), via the 'updatefrommaksapp' template, with a direct link to open the chat.
            </p>
          </div>
        </Card>

        {/* WABA Connect Template Names (Editable) */}
        <Card className="p-6" data-testid="waba-template-names-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-xl bg-indigo-100">
              <MessageSquare className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">WABA Connect Template Names</h2>
                  <p className="text-sm text-slate-500">Configure the template name, message format and variable order for every WhatsApp message</p>
                </div>
                {!tplEditing ? (
                  <Button variant="outline" size="sm" data-testid="edit-template-names-btn" onClick={() => setTplEditing(true)}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="save-template-names-btn" onClick={handleSaveTemplateConfig} disabled={tplSaving}>
                      {tplSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                      Save
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setTplEditing(false); fetchTemplateConfig(); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {[
              { key: 'booking_confirmation', label: 'MAKS Booking — Booking Confirmation', desc: 'Admin Panel, Website, WhatsApp, MAXIO & other internal sources', color: 'bg-green-100 text-green-700' },
              { key: 'booking_confirmation_online', label: 'Online Booking — Booking Confirmation', desc: 'District, KheloMore, Hudle, Email', color: 'bg-sky-100 text-sky-700' },
              { key: 'venue_admin_new_booking', label: 'Venue Admin — New Booking Alert', desc: 'Alerts venue-mapped WhatsApp recipients on MAKS Booking creation', color: 'bg-purple-100 text-purple-700' },
              { key: 'tournament_confirm', label: 'Event Registration Confirmation', desc: 'Sent to players (and partners) on tournament/event registration', color: 'bg-indigo-100 text-indigo-700' },
              { key: 'event_participant_update', label: 'Event Update Notification', desc: 'Manual blast from Event → Send Update dialog', color: 'bg-pink-100 text-pink-700' },
              { key: 'director_upcoming_event', label: '3rd-Party Event — New/Reminder Alert', desc: 'Sent to selected Directors on Venue-Event create/reminder. {{1}} auto-merges Venue - Organizer - Event.', color: 'bg-orange-100 text-orange-700' },
              { key: 'booking_reminder', label: '1-Hour Game Reminder', desc: 'Auto-sent 1 hour before slot time', color: 'bg-amber-100 text-amber-700' },
              { key: 'review_bhayandar', label: 'Review: MAKS Bhayandar', desc: 'Google review request — 15 min after game', color: 'bg-yellow-100 text-yellow-700' },
              { key: 'review_isf', label: 'Review: MAKS ISF', desc: 'Google review request — 15 min after game', color: 'bg-yellow-100 text-yellow-700' },
              { key: 'review_wilfred', label: 'Review: Sports Complex', desc: 'Google review request — 15 min after game', color: 'bg-yellow-100 text-yellow-700' },
            ].map(({ key, label, desc, color }) => {
              const cfg = templateConfig[key] || {};
              const available = cfg.available_params || [];
              const params = cfg.params || [];
              return (
                <div key={key} className="p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid={`tpl-row-${key}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{label}</p>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                    {!tplEditing && (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold font-mono shrink-0 ${color}`}>
                        {cfg.name || templateNames[key] || '-'}
                      </span>
                    )}
                  </div>

                  {tplEditing ? (
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-600">Template Name</Label>
                        <Input
                          data-testid={`tpl-input-${key}`}
                          value={cfg.name || ''}
                          onChange={(e) => updateTplCfg(key, 'name', e.target.value)}
                          className="font-mono text-sm mt-1"
                          placeholder="Approved WABA template name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Message Format (reference body)</Label>
                        <Textarea
                          data-testid={`tpl-body-${key}`}
                          value={cfg.body || ''}
                          onChange={(e) => updateTplCfg(key, 'body', e.target.value)}
                          rows={4}
                          className="text-sm mt-1"
                          placeholder="Hi {{1}}, your booking is confirmed..."
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">
                          Variable Order (click to add/remove — sent left→right as {`{{1}}, {{2}}…`})
                        </Label>
                        <div className="flex flex-wrap gap-2 mt-1" data-testid={`tpl-params-${key}`}>
                          {available.map((p, pi) => {
                            const idx = params.indexOf(p);
                            const active = idx !== -1;
                            return (
                              <button
                                type="button"
                                key={`${key}-${p}-${pi}`}
                                onClick={() => toggleTplParam(key, p)}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                  active
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'
                                }`}
                              >
                                {active ? `{{${idx + 1}}} ` : ''}{p}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {cfg.body && (
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans bg-white p-2 rounded border border-slate-200">{cfg.body}</pre>
                      )}
                      {params.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {params.map((p, i) => (
                            <span key={p} className="px-2 py-0.5 rounded text-[11px] font-mono bg-indigo-50 text-indigo-700 border border-indigo-200">
                              {`{{${i + 1}}}`}={p}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 p-3 rounded-lg border bg-indigo-50 border-indigo-200">
            <p className="text-sm text-indigo-700">
              These template names must match exactly with your approved WABA Connect templates. Changing a name here will immediately affect which template is used for sending messages.
            </p>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
